import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ViewService } from '@base/services/view.service';

import { JsonProduct, SchemeAndPrices } from '@app/baseBtl/catalog/products/interface/context';
import { Plan } from '@app/catalog/products/subscription/plan';
import { ReviewsService } from '@app/services/reviews.service';

import { Observable, concat, mergeMap, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { STOCK_STATUS } from '../../baseBtl/catalog/products/Enum';
import { IBreadcrumb } from '../components/breadcumb/IBreadcrumb';

export interface StructuredOffers {
    '@type': string;
    highPrice?: number;
    lowPrice?: number;
    offerCount?: number;
    priceCurrency: string;
    offers?: any[];
    availability?: string;
    url?: string;
    price?: string;
    itemCondition?: string;
    seller?: any;
}

export interface StructuredBaseProduct {
    '@context': string;
    '@type': string;
    name: string;
    offers?: StructuredOffers;
    category?: string;
}
export interface StructuredProduct extends StructuredBaseProduct {
    '@type': string;
    sku: string;
    mpn: string;
    image: string[];
    color: string;
    description: string;
    brand?: any;
    aggregateRating?: any;
}
export interface StructuredBreadcrumb {
    '@context': string;
    '@type': string;
    itemListElement: any[];
}
@Injectable({
    providedIn: 'any',
})
export class StructuredDataService {
    public stockStatus = new Map();
    public CONDITIONS = {
        0: 'https://schema.org/RefurbishedCondition',
        1: 'https://schema.org/NewCondition',
    };
    private BASE_URL = 'http://www.bouyguestelecom.fr';
    private BASE_DETAIL_URL = 'http://www.bouyguestelecom.fr/telephones-mobiles/';
    private WALL_BASE_URL = 'http://www.bouyguestelecom.fr/telephones-mobiles/telephone-portable-';
    private BASE_URL_STOCK = 'https://schema.org/';

    private readonly router: Router = inject(Router);
    private reviewService: ReviewsService = inject(ReviewsService);
    private viewService: ViewService = inject(ViewService);

    public constructor() {
        this.stockStatus.set(STOCK_STATUS.OUT_OF_STOCK, 'OutOfStock');
        this.stockStatus.set(STOCK_STATUS.IN_STOCK, 'InStock');
        this.stockStatus.set(STOCK_STATUS.SOON_AVAILABLE, 'OutOfStock');
        this.stockStatus.set(STOCK_STATUS.SOON_OUT_OF_STOCK, 'InStock');
        this.stockStatus.set(STOCK_STATUS.ONLY_IN_SHOP, 'InStoreOnly');
        this.stockStatus.set(STOCK_STATUS.IN_STOCK_FROM, 'PreOrder');
    }

    /**
     * Fiche produit
     *
     * @param product
     * @returns
     */
    public getProductData(product): Observable<StructuredProduct> {
        return this.getProductStructure(product);
    }

    /**
     * Liste de produit
     * @todo A utiliser sur le mur ?
     *
     * @param products
     * @returns
     */
    public getProductListData(products) {
        const itemList = [];

        for (const productId in products) {
            const product = products[productId];
            this.getProductStructure(product).subscribe((item) => itemList.push(item));
        }

        return {
            '@context': 'http://schema.org',
            '@type': 'CollectionPage',
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: itemList,
            },
        };
    }

    /**
     * Structure de base des produits
     *
     * @param product
     * @returns
     */
    private getProductStructure(product: JsonProduct): Observable<StructuredProduct> {
        const stockStatus = product.stock?.eresa?.status || 1;
        const stockLabel = `${this.BASE_URL_STOCK}${this.stockStatus.get(stockStatus)}`;

        const productName = `${product.manufacturer} ${product.name} ${product.color || ''} ${
            product.capacity || ''
        }`.trim();
        let imagesUrl;
        if (product.medias && Array.isArray(product.medias)) {
            imagesUrl = product.medias.map((media) => `${this.BASE_URL}/${media}`);
        }
        const productUrl = `${this.BASE_DETAIL_URL}${product.url_key}`;
        const productData: StructuredProduct = {
            '@context': 'http://schema.org',
            '@type': 'Product',
            sku: product.sku,
            mpn: product.sku,
            name: productName,
            image: imagesUrl,
            description: product.meta_description,
            color: product.color,
            brand: {
                '@type': 'Brand',
                name: product.manufacturer,
                url: '',
            },
            category: 'smartphone',
            offers: {
                '@type': 'Offer',
                url: productUrl,
                price: this.formatPrice(product.prices.premium.base_price),
                priceCurrency: 'EUR',
                itemCondition: '',
                availability: stockLabel,
                seller: {
                    '@type': 'Organization',
                    name: 'Bouygues Telecom',
                },
            },
        };

        productData.brand = Object.assign({}, productData.brand, {
            url: `${this.WALL_BASE_URL}${product.manufacturer_code}`,
        });
        productData.offers.itemCondition = /recondition/i.test(product.name) ? this.CONDITIONS[0] : this.CONDITIONS[1];

        const schemeObs$ = this.viewService.schemeAndPricesSubject.pipe(
            map((schemeAndPrices: SchemeAndPrices) => {
                productData.offers.price = this.formatPrice(schemeAndPrices.prices.selectedFunding.apportInitial);
                return productData;
            }),
        );

        const reviewObs$ = timer(300).pipe(
            mergeMap(() => this.reviewService.getReviews(product.gencode)),
            map((reviewData) => {
                let bestRating = 5;
                if (product.bazaarvoice && product.bazaarvoice.rating) {
                    bestRating = parseFloat(product.bazaarvoice.best);
                }
                productData.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: reviewData.note,
                    ratingCount: reviewData.reviewCount,
                    bestRating,
                };

                return productData;
            }),
        );

        return concat(schemeObs$.pipe(take(1)), reviewObs$);
    }

    /**
     * Structure de base
     *
     * @param name
     * @param description
     * @returns
     */
    public getWebsiteData(name, description) {
        return {
            '@context': 'http://schema.org',
            '@type': 'Website',
            name,
            description,
        };
    }

    /**
     * Structure des breadcrumbs
     *
     * @param breadcrumbsList
     * @returns
     */
    public setBreadcrumbData(breadcrumbsList: IBreadcrumb[]): StructuredBreadcrumb {
        const jsonLD: StructuredBreadcrumb = {
            '@context': 'http://schema.org/',
            '@type': 'BreadcrumbList',
            itemListElement: [],
        };
        const siteUri = 'https://www.bouyguestelecom.fr';
        let i;
        for (i = 0; i < breadcrumbsList.length - 1; ++i) {
            const item = {
                '@type': 'ListItem',
                position: i + 1,
                name: breadcrumbsList[i].label,
                item: {
                    '@id': siteUri + breadcrumbsList[i].url,
                    '@type': 'Thing',
                },
            };
            jsonLD.itemListElement.push(item);
        }

        const temp = {
            '@type': 'ListItem',
            position: i + 1,
            name: breadcrumbsList[i].label,
            item: {
                '@id': siteUri + this.router.url,
                '@type': 'Thing',
            },
        };
        jsonLD.itemListElement.push(temp);
        return jsonLD;
    }

    /**
     * Structure Plan B&You
     *
     * @param Plan
     * @returns
     */
    public setPlanData(plan: Plan): StructuredBaseProduct {
        const jsonLD: StructuredBaseProduct = {
            '@context': 'http://schema.org/',
            '@type': 'Product',
            name: plan.dataEnvelope,
            offers: {
                '@type': 'AggregateOffer',
                highPrice: 0,
                lowPrice: 0,
                offerCount: 1,
                priceCurrency: 'EUR',
                offers: [],
            },
        };

        jsonLD.offers.highPrice = plan && plan.oldPrice ? this.numberUtil(plan.oldPrice) : 0;
        jsonLD.offers.lowPrice = plan && plan.price ? this.numberUtil(plan.price) : 0;
        jsonLD.offers.offers.push({
            '@type': 'Offer',
            price: plan.price,
        });
        return jsonLD;
    }
    // keep the same fraction as front;
    public numberUtil(price: number): number {
        return Number(parseFloat(price.toString()).toFixed(2));
    }
    // keep two digits for float
    private formatPrice(price: number): string {
        return Number(price).toFixed(2);
    }
}
